import { useEffect, useRef } from 'react';
import { useGetCurrentUserQuery, type IdentityType, type PublicIdentity } from '@loci/api';
import { useNotification } from '@loci/store';

/**
 * Window (ms) during which the newest identity is considered "just created".
 *
 * The `/auth/me` cache means this hook runs on every authenticated page load,
 * so it must avoid firing on every mount. Anchoring on a short window ties
 * the toast to the actual OAuth return that just attached the identity.
 */
export const JUST_LINKED_WINDOW_MS = 30_000;

const PROVIDER_LABELS: Partial<Record<IdentityType, string>> = {
  OAUTH_GOOGLE: 'Google',
  OAUTH_APPLE: 'Apple',
  OAUTH_GITHUB: 'GitHub',
  OAUTH_FACEBOOK: 'Facebook',
};

const OAUTH_TYPES: ReadonlySet<IdentityType> = new Set<IdentityType>([
  'OAUTH_GOOGLE',
  'OAUTH_APPLE',
  'OAUTH_GITHUB',
  'OAUTH_FACEBOOK',
]);

function parseCreatedAt(identity: PublicIdentity): number | null {
  const ts = Date.parse(identity.createdAt);
  return Number.isFinite(ts) ? ts : null;
}

function newestIdentity(identities: ReadonlyArray<PublicIdentity>): PublicIdentity | null {
  let newest: PublicIdentity | null = null;
  let newestTs = -Infinity;
  for (const identity of identities) {
    const ts = parseCreatedAt(identity);
    if (ts === null) continue;
    if (ts > newestTs) {
      newest = identity;
      newestTs = ts;
    }
  }
  return newest;
}

/**
 * Options accepted by {@link useJustLinkedToast}. `skip` threads the caller's
 * anonymous-visitor guard through to the underlying RTK Query subscription so
 * we never fire a guaranteed-401 `/auth/me` request on public pages. `now` is
 * exposed so tests can inject a deterministic clock.
 */
export interface UseJustLinkedToastOptions {
  now?: () => number;
  skip?: boolean;
}

/**
 * Surface a one-shot toast when the backend just attached a new OAuth
 * identity to an existing LOCI account ("linked" decision path).
 *
 * Fires when:
 * 1. `/api/v1/auth/me` returns more than one identity, and
 * 2. the most recent identity is an OAuth type, and
 * 3. that identity was created within {@link JUST_LINKED_WINDOW_MS}.
 *
 * Each identity id is surfaced at most once per client session, so React
 * re-renders, route changes, and RTK Query refetches do not re-open the
 * toast.
 *
 * See `docs/auth/oauth-account-linking-policy.md` for the broader context.
 */
export function useJustLinkedToast(options: UseJustLinkedToastOptions = {}): void {
  const { now = Date.now, skip = false } = options;
  const { data } = useGetCurrentUserQuery(undefined, { skip });
  const { showNotification } = useNotification();
  const shownIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!data || !('identities' in data)) return;

    const { identities } = data;
    if (!identities || identities.length < 2) return;

    const newest = newestIdentity(identities);
    if (!newest) return;
    if (!OAUTH_TYPES.has(newest.type)) return;

    const createdAt = parseCreatedAt(newest);
    if (createdAt === null) return;
    if (now() - createdAt > JUST_LINKED_WINDOW_MS) return;

    if (shownIdsRef.current.has(newest.id)) return;
    shownIdsRef.current.add(newest.id);

    const providerLabel = PROVIDER_LABELS[newest.type] ?? 'your account';

    showNotification({
      type: 'success',
      title: `Linked your ${providerLabel} account to your existing LOCI account`,
      priority: 'normal',
      metadata: { source: 'oauth-link', identityId: newest.id },
    });
  }, [data, now, showNotification]);
}
